import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import MarkEmailUnreadIcon from "@material-ui/icons/MarkunreadOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PersonIcon from "@material-ui/icons/Person";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import ReceiptIcon from "@material-ui/icons/Receipt";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import Reply from "./Reply";
import "./conversations.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The list and its count used to be two separate round trips (as in
 * every other pre-redesign screen); combined into one query here, the
 * same way the redesigned Promotions and API keys screens do it.
 * ------------------------------------------------------------------ */

const CONVERSATION_FIELDS = `
  _id
  title
  closed
  user {
    _id
    fullName
    phoneNumber
  }
  driver {
    _id
    fullName
    phoneNumber
  }
  conversationCategory
  conversationType
  adminUnreadCount
  order {
    _id
    status
    finalPrice
    createdAt
    user {
      _id
      fullName
      phoneNumber
    }
    tracking {
      trackId
    }
    cart {
      products {
        quantity
        product {
          _id
          title
        }
      }
    }
  }
  lastMessage {
    text
  }
  messages {
    _id
    text
    senderType
    user {
      _id
      fullName
    }
    driver {
      _id
      fullName
    }
    messageType
    createdAt
    updatedAt
  }
  createdAt
`;

const GET_CONVERSATIONS = gql`
  query getConversations(
    $limit: Int
    $skip: Int
    $conversationCategory: ConversationCategory
    $conversationType: ConversationType
    $driverName: String
    $passengerName: String
    $repliedByAdmin: Boolean
    $closed: Boolean
    $title: String
    $createdAtSort: Int
    $createdAtFrom: Date
    $createdAt: Date
  ) {
    getConversations(
      Pagination: { limit: $limit, skip: $skip }
      filters: {
        conversationCategory: $conversationCategory
        conversationType: $conversationType
        passengerName: $passengerName
        driverName: $driverName
        repliedByAdmin: $repliedByAdmin
        closed: $closed
        title: $title
        createdAtFrom: $createdAtFrom
        createdAt: $createdAt
      }
      sort: { createdAt: $createdAtSort }
    ) {
      ${CONVERSATION_FIELDS}
    }
    getConversationsCount(
      filters: {
        conversationCategory: $conversationCategory
        conversationType: $conversationType
        passengerName: $passengerName
        driverName: $driverName
        repliedByAdmin: $repliedByAdmin
        closed: $closed
        title: $title
        createdAtFrom: $createdAtFrom
        createdAt: $createdAt
      }
    )
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_CONFIG = {
  unreplied: {
    repliedByAdmin: false,
    closed: false,
    title: "Unreplied conversations",
    description: "Customers and drivers who are still waiting on your first reply.",
    emptyTitle: "Nothing waiting on you",
    emptyMessage: "Every conversation has at least one reply from your shop.",
    replyLabel: "Reply",
    readOnly: false,
  },
  replied: {
    repliedByAdmin: true,
    closed: false,
    title: "Replied conversations",
    description: "Open tickets you've already replied to — watch for new messages here.",
    emptyTitle: "No open replied tickets",
    emptyMessage: "Replied conversations that are still open will show up here.",
    replyLabel: "Reply",
    readOnly: false,
  },
  closed: {
    repliedByAdmin: true,
    closed: true,
    title: "Closed conversations",
    description: "Resolved conversations, kept for your records.",
    emptyTitle: "No closed conversations yet",
    emptyMessage: "Conversations move here once they're resolved.",
    replyLabel: "View",
    readOnly: true,
  },
};

const CATEGORY_OPTIONS = [
  { value: "MESSAGE", label: "Message" },
  { value: "SUPPORT_TICKET", label: "Support ticket" },
];
const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const TYPE_OPTIONS = [
  { value: "RIDE", label: "Ride" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "FOOD", label: "Food" },
  { value: "GROCERY", label: "Grocery" },
  { value: "SUPPORT", label: "Support" },
  { value: "SHOP_SUPPORT", label: "Shop support" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const FILTER_DEBOUNCE_MS = 350;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const ConversationsList = ({ status }) => {
  const config = STATUS_CONFIG[status];

  const [titleFilter, setTitleFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [openConversation, setOpenConversation] = useState(null);
  const [orderDetailTarget, setOrderDetailTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: -1,
    repliedByAdmin: config.repliedByAdmin,
    closed: config.closed,
  });

  // Every text/select/date filter shares one debounce instead of requiring
  // an explicit "Search" click (and the "pick at least one filter" modal
  // that used to block an empty search) — typing just narrows the list.
  useEffect(() => {
    const handler = setTimeout(() => {
      setVariables((v) => ({
        ...v,
        skip: 0,
        title: titleFilter.trim() || undefined,
        passengerName: customerFilter.trim() || undefined,
        driverName: driverFilter.trim() || undefined,
        conversationCategory: categoryFilter || undefined,
        conversationType: typeFilter || undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [titleFilter, customerFilter, driverFilter, categoryFilter, typeFilter, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_CONVERSATIONS, {
    variables,
    fetchPolicy: "network-only",
  });

  const [fetchAllForExport, { data: exportData, error: exportQueryError }] = useLazyQuery(GET_CONVERSATIONS, {
    fetchPolicy: "network-only",
  });

  const conversations = (data && data.getConversations) || [];
  const totalCount = (data && data.getConversationsCount) || 0;
  const unreadOnPage = conversations.reduce((sum, c) => sum + (c.adminUnreadCount || 0), 0);

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (titleFilter.trim()) activeFilterChips.push({ id: "title", label: `Title: ${titleFilter}` });
  if (customerFilter.trim()) activeFilterChips.push({ id: "customer", label: `Customer: ${customerFilter}` });
  if (driverFilter.trim()) activeFilterChips.push({ id: "driver", label: `Driver: ${driverFilter}` });
  if (categoryFilter) {
    activeFilterChips.push({ id: "category", label: `Category: ${CATEGORY_LABELS[categoryFilter]}` });
  }
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "title") setTitleFilter("");
    if (id === "customer") setCustomerFilter("");
    if (id === "driver") setDriverFilter("");
    if (id === "category") setCategoryFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setTitleFilter("");
    setCustomerFilter("");
    setDriverFilter("");
    setCategoryFilter("");
    setTypeFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportQueryError) return;
    setExporting(false);
    setExportError(readableError(exportQueryError));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportQueryError]);

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [["Title", "Customer", "Driver", "Category", "Type", "Unread", "Created"]];
    exportData.getConversations.forEach((c) => {
      rows.push([
        c.title,
        (c.user && c.user.fullName) || "-",
        (c.driver && c.driver.fullName) || "-",
        CATEGORY_LABELS[c.conversationCategory] || c.conversationCategory,
        TYPE_LABELS[c.conversationType] || c.conversationType,
        c.adminUnreadCount || 0,
        moment(c.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Conversations");
    XLSX.writeFile(wb, `${config.title.replace(/\s+/g, "-")}.xlsx`);
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExportError("");
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page conversationsPage">
        <main className="main">
          <PageHeader title={config.title} description={config.description} />
          <MetricsSkeleton count={2} />
          <TableSkeleton rows={6} columns={5} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page conversationsPage">
      <main className="main">
        {/* HEADER */}
        <PageHeader
          title={config.title}
          count={totalCount}
          description={config.description}
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} conversation${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        {/* STATS */}
        <section className="stats">
          <MetricCard icon={<ChatBubbleOutlineIcon />} title="Total" value={totalCount} />
          <MetricCard
            icon={<MarkEmailUnreadIcon />}
            title="Unread messages"
            value={unreadOnPage}
            tone={unreadOnPage > 0 ? "warning" : "default"}
            hint="On this page"
          />
        </section>

        {/* FILTERS */}
        <section className="filters">
          <SearchField
            placeholder="Search by ticket title..."
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
          />
          <div className="filtersSelects">
            <SearchField
              placeholder="Customer name..."
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            />
            <SearchField
              placeholder="Driver name..."
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
            />
            <FilterDropdown
              label={categoryFilter ? `Category: ${CATEGORY_LABELS[categoryFilter]}` : "Category"}
              options={[{ value: "", label: "All categories" }, ...CATEGORY_OPTIONS]}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList filters={activeFilterChips} onClearFilter={clearFilter} onClearAll={clearAllFilters} />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft" />
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh conversations"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {exportError && (
          <div className="dsFormMessage error" role="alert">
            {exportError}
          </div>
        )}

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Conversation</div>
              <div className="tableHeaderCell">From</div>
              <div className="tableHeaderCell">Category / type</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {conversations.length === 0 ? (
              <EmptyState icon={<ChatBubbleOutlineIcon />} title={config.emptyTitle} message={config.emptyMessage} />
            ) : (
              conversations.map((conversation) => {
                const person = conversation.user || conversation.driver;
                const isDriver = Boolean(conversation.driver) && !conversation.user;
                const unread = conversation.adminUnreadCount || 0;

                return (
                  <div
                    className={`row conversationRow ${unread > 0 ? "isUnread" : ""}`}
                    key={conversation._id}
                    onClick={() => setOpenConversation(conversation)}
                  >
                    <div className="conversationCell" data-label="Conversation">
                      <h4>
                        {conversation.title}
                        {unread > 0 && (
                          <StatusBadge status="warning" label={`${unread} unread`} dot={false} />
                        )}
                      </h4>
                      <p>{(conversation.lastMessage && conversation.lastMessage.text) || "No messages yet"}</p>
                    </div>

                    <div className="conversationFromCell" data-label="From">
                      {isDriver ? <LocalShippingIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                      <div className="conversationFromText">
                        <h4>{(person && person.fullName) || "Unknown"}</h4>
                        <p>{(person && person.phoneNumber) || (isDriver ? "Driver" : "Customer")}</p>
                      </div>
                    </div>

                    <span data-label="Category / type">
                      <span className="conversationTag">
                        {CATEGORY_LABELS[conversation.conversationCategory] || conversation.conversationCategory}
                      </span>
                      <span className="conversationTag muted">
                        {TYPE_LABELS[conversation.conversationType] || conversation.conversationType}
                      </span>
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(conversation.createdAt)}</strong>
                      <span>{formatTime(conversation.createdAt)}</span>
                    </span>

                    <div className="actions">
                      {conversation.order && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderDetailTarget(conversation.order);
                          }}
                          title="View order"
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenConversation(conversation);
                        }}
                        title={config.replyLabel}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {/* PAGINATION */}
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* CONVERSATION */}
      <DialogShell
        open={Boolean(openConversation)}
        onClose={() => setOpenConversation(null)}
        title={openConversation ? openConversation.title : ""}
        subtitle={
          openConversation
            ? `${(openConversation.user && openConversation.user.fullName) ||
                (openConversation.driver && openConversation.driver.fullName) ||
                "Unknown"} · ${formatDate(openConversation.createdAt)}`
            : ""
        }
        size="lg"
      >
        {openConversation && (
          <React.Fragment>
            {openConversation.order && (
              <button
                type="button"
                className="conversationViewOrderLink"
                onClick={() => setOrderDetailTarget(openConversation.order)}
              >
                <ReceiptIcon fontSize="small" /> View linked order
              </button>
            )}
            <Reply
              conversation={openConversation}
              readOnly={config.readOnly}
              onSent={() => refetch()}
            />
          </React.Fragment>
        )}
      </DialogShell>

      {/* LINKED ORDER */}
      <DialogShell
        open={Boolean(orderDetailTarget)}
        onClose={() => setOrderDetailTarget(null)}
        title="Order details"
        subtitle={orderDetailTarget ? `#${orderDetailTarget._id.slice(-8).toUpperCase()}` : ""}
      >
        {orderDetailTarget && <OrderDetails order={orderDetailTarget} />}
      </DialogShell>
    </div>
  );
};

export default ConversationsList;
